import { useCallback } from 'react'
import * as React from 'react'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import moment from 'moment'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Heading from 'LuxKit/Typography/Heading'
import Step from 'LuxKit/Steppers/Step'
import TextButton from 'LuxKit/Button/TextButton'
import ContactUSP from '../ContactUSP'
import Container from 'components/utils/Sheet'
import { useIntl } from 'react-intl'
interface Props {
  open: boolean
  onClose: () => void
  modalType?: 'bnbl' | 'bestPrice' | 'gifting'
  bookBy: moment.Moment
  travelUntil: moment.Moment
  onViewPackageOptions?: () => void
}

function USPModal(props: Props) {
  const { open, onClose, modalType = '', bookBy, travelUntil, onViewPackageOptions } = props
  const intl = useIntl()

  const isBestPrice = modalType === 'bestPrice'

  const modalTitleBnbl = intl.formatMessage({
    id: 'JVHZZWU353yQgdX4GK3A',
    defaultMessage: 'Buy now, choose dates later',
    description: 'Buy now choose dates later modal title in the date selection or calendar picker',
  })

  const modalTitleBestPrice = intl.formatMessage({
    id: '5oXQj-2XMwHW2yIgUybV',
    defaultMessage: 'Our best price guarantee',
    description: 'Best price guarantee modal title in the pricing or cost breakdown section',
  })

  const modalTitleGifting = intl.formatMessage({
    id: 'TQWFp5mlot2vzw-LPxsu',
    defaultMessage: 'Gifting available',
    description: 'Modal title indicating that gifting is available for the current offer or experience',
  })

  const modalTitleWePriceMatch = intl.formatMessage({
    id: '8tj83LVJbWtlwFVZ07N6',
    defaultMessage: 'We price match',
    description: 'Modal title for the price match guarantee information and claim details',
  })

  const modalTitles: Record<string, string> = {
    bnbl: modalTitleBnbl,
    bestPrice: modalTitleBestPrice,
    gifting: modalTitleGifting,
  }

  const title = isBestPrice ? modalTitleWePriceMatch : modalTitles[modalType]

  const handleViewPackageOptions = useCallback(() => {
    onViewPackageOptions?.()
    onClose()
  }, [onViewPackageOptions, onClose])

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
    >
      {modalType === 'bnbl' && <VerticalSpacer gap={16}>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'klP_PapZndmVeTcrMpgV',
            defaultMessage: 'Our offers are live for a limited time. Secure your dream escape at a great price! No dates yet? Reserve the price now and pick dates later.',
            description: 'Buy now choose dates later intro text in the date selection or calendar picker',
          })}
        </BodyText>

        <Heading variant="heading5" style={{ marginTop: 24 }}>
          {intl.formatMessage({
            id: 'dyMCW8ljLwM1OgCSjl4W',
            defaultMessage: 'How it works',
            description: 'Section heading for the how it works explanation steps on the landing page',
          })}
        </Heading>

        <Group direction="vertical" gap={8}>
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Step number={1} label="" state="inactive" />
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">
                {intl.formatMessage({
                  id: 'wL2VtTKBpV1IXHVKcseC',
                  defaultMessage: 'Secure your escape today',
                  description: 'Step 1 title for buy now choose dates later on the offer detail page',
                })}
              </BodyText>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'E2Kgfa8n0Q5_x3ePfVHh',
                  defaultMessage: 'Purchase now to lock in the price.',
                  description: 'Step 1 description for buy now choose dates later on the offer detail page',
                })}
              </BodyText>
            </Group>
          </Group>
          <VerticalSpacer gap={8} />
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Step number={2} label="" state="inactive" />
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">
                {intl.formatMessage({
                  id: '6W01a2qIWkmIJiK-NJvw',
                  defaultMessage: 'Choose dates later',
                  description: 'Step 2 title for buy now choose dates later on the offer detail page',
                })}
              </BodyText>
              <BodyText variant="medium">
                {intl.formatMessage(
                  {
                    id: 'oDW8Z9fDcZe0nB0hZQtV',
                    defaultMessage: 'Pick dates anytime before <strong>{bookByDate}</strong> in your account.',
                    description: 'Step 2 description for buy now choose dates later on the offer detail page',
                  },
                  {
                    bookByDate: moment(bookBy).format(DMY_CASUAL_FORMAT),
                    strong: (chunks: React.ReactNode) => <strong key="strong">{chunks}</strong>,
                  },
                )}
              </BodyText>
            </Group>
          </Group>
          <VerticalSpacer gap={8} />
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Step number={3} label="" state="inactive" />
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">
                {intl.formatMessage({
                  id: 'DhGzk3LynQd5yHXJUpYC',
                  defaultMessage: 'Manage your booking online',
                  description: 'Step 3 title for buy now choose dates later in the booking flow',
                })}
              </BodyText>
              <BodyText variant="medium">
                {intl.formatMessage(
                  {
                    id: 'xmLRO1aee_FFGyTl7cmz',
                    defaultMessage: 'Make sure to travel by <strong>{travelUntilDate}</strong>.',
                    description: 'Step 3 description for buy now choose dates later on the offer detail page',
                  },
                  {
                    travelUntilDate: moment(travelUntil).format(DMY_CASUAL_FORMAT),
                    strong: (chunks: React.ReactNode) => <strong key="strong">{chunks}</strong>,
                  },
                )}
              </BodyText>
            </Group>
          </Group>
        </Group>

        <MessageBanner kind="informational-primary" density="compact">
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <LineInfoCircleIcon />
            <BodyText variant="medium">
              {intl.formatMessage({
                id: 'tjJO5idbAG3eOfkTWpbH',
                defaultMessage: 'Subject to availability and surcharges (peak period or property-specific). Any surcharges will be shown before confirming your dates.',
                description: 'Buy now choose dates later disclaimer text on the offer detail page',
              })}
            </BodyText>
          </Group>
        </MessageBanner>

      </VerticalSpacer>}
      {modalType === 'bestPrice' && <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'wVOXQR_Frh3G5C-exymG',
            defaultMessage: 'We negotiate the best prices so you don’t have to. In the unlikely event that you find a better price for the same offer, we promise to match it.',
            description: 'Best price guarantee intro text on the offer listing or detail page',
          })}
        </BodyText>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'wW58AnOZcoq1GuAbQiXz',
              defaultMessage: '1. Compare the same offer',
              description: 'Step 1 title for best price guarantee on the offer listing or detail page',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'dOKvOjGBIzu3s4WNIpvj',
              defaultMessage: 'Make sure the offer you’ve found is for the same hotel with the same VIP inclusions as our package.',
              description: 'Step 1 description for best price guarantee on the hotel page',
            })}
          </BodyText>
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'mG9rwbcMBQCT9S8EDXTH',
              defaultMessage: '2. Record the better price',
              description: 'Step 2 title for best price guarantee in the pricing or cost breakdown section',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'PMgO9oqg_lQqgKOPn5MW',
              defaultMessage: 'Take a website screenshot or a photo of a catalogue so you can send it to us.',
              description: 'Step 2 description for best price guarantee in the price breakdown',
            })}
          </BodyText>
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'RD5OGujRGeG14PaXdPeU',
              defaultMessage: '3. We’ll match the price',
              description: 'Step 3 title for best price guarantee in the pricing or cost breakdown section',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'jl_hLp-AO0Vg-wDJTciB',
              defaultMessage: 'Once verified we’ll match the price.',
              description: 'Step 3 description for best price guarantee in the price breakdown',
            })}
          </BodyText>
        </Group>
      </VerticalSpacer>}
      {modalType === 'gifting' && <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          {intl.formatMessage(
            {
              id: 'ARX7BhHwZDsnHViIdYfb',
              defaultMessage: 'Buy a package and send as a gift to a special someone. They must book by {bookByDate} and can travel until {travelUntilDate}.',
              description: 'Introductory text explaining the gifting feature with booking and travel deadline dates',
            },
            {
              bookByDate: <FormatDateTime key="bookBy" value={bookBy} format="dmy-casual" />,
              travelUntilDate: <FormatDateTime key="travelUntil" value={travelUntil} format="dmy-casual" />,
            },
          )}
        </BodyText>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'AXUCgS_DeLrOPR-eQ3Qv',
              defaultMessage: '1. Purchase online',
              description: 'Step one title in the gifting how it works section for purchasing online',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'dD0jUFLqdr2WbqQk-2ou',
              defaultMessage: 'Purchasing this as a gift will secure the offer for your recipient and give them the flexibility to choose travel dates at a later time. Dates must be within the book by and validity period, and are subject to availability and surcharges.',
              description: 'Step 1 description for gifting on the gift card purchase or redemption page',
            })}
          </BodyText>
        </Group>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'QtYHs31QY-LE-Aqih01-',
              defaultMessage: '2. Send or schedule your gift',
              description: 'Step two title in the gifting how it works section for sending or scheduling',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'H-0ATtHObjbK5s_D-4p_',
              defaultMessage: 'Provide the recipient’s details and schedule a date so we can email it to them on your behalf.',
              description: 'Step 2 description for gifting on the gift card purchase or redemption page',
            })}
          </BodyText>
        </Group>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: '5mCO_DXXyOc1pUu0Bcta',
              defaultMessage: '3. The world is their oyster',
              description: 'Step three title in the gifting how it works section for recipient travel options',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'KzlxS_BmvFaoktqv6vV0',
              defaultMessage: 'Your recipient can manage and make changes to their booking according to our 7-day ‘change of mind’ guarantee after booking (if applicable – refer to individual offer for more information).',
              description: 'Step 3 description for gifting on the gift card purchase or redemption page',
            })}
          </BodyText>
        </Group>
      </VerticalSpacer>}

      <Container style={{ marginTop: 24 }}>
        <ContactUSP
          title={intl.formatMessage({
            defaultMessage: 'Need help?',
            id: 'FaJJ8_UbX2oa1A9VGlQB',
            description: 'Title for the contact support section in the flash hotel USP modal',
          })}
          description={intl.formatMessage({
            defaultMessage: 'Our team are available 24 hours, 7 days',
            id: 'HlztxSr6aeMNsFgTRc4q',
            description: 'Contact USP description about team availability on the offer detail page',
          })}
        />
      </Container>

      {
        onViewPackageOptions && (
          <VerticalSpacer gap={24}>
            <Group direction="horizontal" horizontalAlign="end">
              <TextButton
                kind="primary"
                onClick={handleViewPackageOptions}
                fit="mobile-full-width"
                style={{
                  marginTop: 16,
                }}
              >
                {intl.formatMessage({
                  id: '6qKTMniKyl2vavx0PY0y',
                  defaultMessage: 'View package options',
                  description: 'View package options button text on the offer detail page',
                })}
              </TextButton>
            </Group>
          </VerticalSpacer>
        )
      }

    </Modal>
  )
}

export default USPModal
